import Vue from 'vue';

/**
 * AI 재작성 세션 관리 스토어
 *
 * 이 스토어는 에디터에서 AI 재작성/작성 모달의 상태를 관리합니다.
 * 하나의 콘텐츠(텍스트 블록, 비즈니스 케이스 등)당 하나의 세션이 생성되며,
 * 여러 세션이 동시에 존재할 수 있습니다 (멀티 세션).
 *
 * 주요 개념:
 * - sessions: reportCode → contentId → session 2단계 구조
 *   보고서 전환 시 reportCode로 필터링하여 현재 보고서의 세션만 표시합니다.
 * - chatLoadingCount: AiHelper(채팅) 컴포넌트의 로딩 카운트 (세션과 별도로 관리)
 * - pendingRestore: 다른 주제의 세션을 복원할 때, 주제 이동 완료 후 모달을 열기 위한 대기열
 *
 * 영속화:
 * - store/index.js의 IndexedDB 플러그인이 이 모듈 전체를 자동 저장합니다.
 * - 새로고침 시 IndexedDB에서 복원된 후 SANITIZE_RESTORED가 호출되어
 *   완료/실패 세션만 보존하고, 진행 불가능한 세션(idle/loading)은 삭제합니다.
 *
 * 연관 컴포넌트:
 * - AiRewriteButton.vue: 세션 등록(REGISTER_SESSION) + 모달 표시
 * - AiWriterPanel.vue: AI 요청 전송, 응답/에러 상태 감시
 * - AiRewriteMinimizedStack.vue: 최소화된 세션 카드 UI + 복원 분기 로직
 * - WriteStoryBoard.vue: pendingRestore 감시, 페이지 이동 차단
 * - WriteNav.vue: 목차 이동 차단, ai-navigate-to-topic 이벤트 처리
 * - aiRewriteWorkerManager.js: Web Worker를 통한 비동기 API 요청 → SET_RESPONSE/SET_ERROR 커밋
 */

/**
 * contentId로 세션을 검색하는 헬퍼.
 * reportCode를 모르는 mutation/getter에서 사용합니다.
 * @returns {{ session: object, reportCode: string } | null}
 */
function findSessionEntry(sessions, contentId) {
  for (const reportCode of Object.keys(sessions)) {
    const group = sessions[reportCode];
    if (group[contentId]) {
      return { session: group[contentId], reportCode };
    }
  }
  return null;
}

/** 모든 보고서의 세션을 평탄화하여 배열로 반환 */
function allSessions(sessions) {
  const result = [];
  for (const reportCode of Object.keys(sessions)) {
    const group = sessions[reportCode];
    for (const contentId of Object.keys(group)) {
      result.push(group[contentId]);
    }
  }
  return result;
}

export default {
  namespaced: true,

  state: {
    /**
     * reportCode → contentId → session 2단계 구조
     * 예: { 'RPT_001': { 123: { contentId: 123, status: 'completed', ... } } }
     */
    sessions: {},
    /** AiHelper 채팅 컴포넌트의 로딩 요청 수 (sessions와 별도 추적) */
    chatLoadingCount: 0,
    /**
     * 다른 주제의 세션을 복원할 때 사용하는 대기열.
     * 주제 이동 → disclosures 로드 완료 → AiRewriteButton 마운트 후
     * WriteStoryBoard의 disclosures watcher가 이 값을 확인하여 RESTORE_SESSION을 실행합니다.
     */
    pendingRestore: null,
    /**
     * '이걸로 본문 수정하기' 시 사용하는 대기열.
     * 다른 주제의 세션을 적용할 때, 주제 이동 → disclosures 로드 후
     * WriteStoryBoard의 disclosures watcher가 이 값을 소비하여 handleAiRewrite를 호출합니다.
     * { contentId, aiResponse }
     */
    pendingApply: null,
    /**
     * DataTable 적용 후 주제 이동 완료 시 스크롤할 대상.
     * 다른 주제의 DataTable 적용 → 주제 이동 → disclosures 로드 후
     * WriteStoryBoard의 disclosures watcher가 이 값을 소비하여 해당 콘텐츠로 스크롤합니다.
     * 값: contentId 문자열 (예: 'dt_123')
     */
    pendingScrollTo: null
  },

  mutations: {
    /**
     * AI 재작성 세션을 등록합니다.
     * AiRewriteButton.openModal()에서 호출됩니다.
     *
     * @param {string|number} contentId - 콘텐츠 ID (텍스트: number, BC: 'bc-{id}')
     * @param {string} reportCode - 보고서 코드 (필수, 세션 그룹핑 키)
     * @param {string|null} lv1Key - 현재 주제 lv1 key (새로고침 후 주제 이동에 사용)
     * @param {string|null} lv2Key - 현재 주제 lv2 key (새로고침 후 주제 이동에 사용)
     * @param {string|null} lv1Name - 현재 주제 lv1 이름 (대주제, UI 표시용)
     * @param {string|null} lv2Name - 현재 주제 lv2 이름 (소주제, UI 표시용)
     *
     * status lifecycle: 'idle' → 'loading' → 'completed' | 'failed'
     *   - idle: 모달 열림, 아직 AI 요청 전
     *   - loading: AI 요청 중 (Worker가 처리 중)
     *   - completed: AI 응답 수신 완료
     *   - failed: AI 요청 실패
     */
    REGISTER_SESSION(
      state,
      {
        contentId,
        reportCode,
        originalText = '',
        lv1Key = null,
        lv2Key = null,
        lv1Name = null,
        lv2Name = null,
        type = 'rewrite',
        dataTableId = null,
        orderIndex = null
      }
    ) {
      if (!reportCode) return;
      // 보고서 그룹이 없으면 생성
      if (!state.sessions[reportCode]) {
        Vue.set(state.sessions, reportCode, {});
      }
      // 이미 등록된 세션이면 중복 생성 방지
      if (state.sessions[reportCode][contentId]) return;
      Vue.set(state.sessions[reportCode], contentId, {
        contentId,
        reportCode,
        status: 'idle',
        isMinimized: false,
        aiResponse: null,
        error: null,
        requestText: '',
        requestedMode: null,
        originalText,
        toastShown: false,
        lv1Key,
        lv2Key,
        lv1Name,
        lv2Name,
        type,
        dataTableId,
        orderIndex,
        createdAt: Date.now()
      });
    },

    UNREGISTER_SESSION(state, { contentId }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (!entry) return;
      Vue.delete(state.sessions[entry.reportCode], contentId);
      // 빈 보고서 그룹 정리
      if (Object.keys(state.sessions[entry.reportCode]).length === 0) {
        Vue.delete(state.sessions, entry.reportCode);
      }
    },

    /** beforeRouteLeave에서 페이지를 떠날 때 모든 세션을 정리합니다. */
    CLEAR_ALL_SESSIONS(state) {
      Object.keys(state.sessions).forEach(reportCode => {
        Vue.delete(state.sessions, reportCode);
      });
    },

    /**
     * 페이지 이동 시 호출.
     * - idle/loading 세션: Worker 소멸로 재개 불가 → 삭제
     * - completed/failed 세션: 결과 보존 → 최소화 상태로 유지
     * IndexedDB 플러그인이 자동 저장하므로, 복귀 시 SANITIZE_RESTORED를 통해 복원됩니다.
     */
    PREPARE_FOR_LEAVE(state) {
      Object.keys(state.sessions).forEach(reportCode => {
        const group = state.sessions[reportCode];
        Object.keys(group).forEach(contentId => {
          const session = group[contentId];
          if (session.status === 'completed' || session.status === 'failed') {
            session.isMinimized = true;
          } else {
            Vue.delete(group, contentId);
          }
        });
        if (Object.keys(group).length === 0) {
          Vue.delete(state.sessions, reportCode);
        }
      });
      state.chatLoadingCount = 0;
      state.pendingRestore = null;
      state.pendingApply = null;
      state.pendingScrollTo = null;
    },

    /** aiRewriteWorkerManager에서 Worker 요청 전 status를 'loading'으로 변경합니다. */
    SET_STATUS(state, { contentId, status }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.status = status;
      }
    },

    SET_MINIMIZED(state, { contentId, isMinimized }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.isMinimized = isMinimized;
      }
    },

    /**
     * 최소화된 세션을 복원합니다.
     * 한 번에 하나의 모달만 열릴 수 있으므로, 다른 열린 모달을 먼저 최소화합니다.
     * AiRewriteButton의 watch('session.isMinimized')가 변경을 감지하여 $bvModal.show()를 호출합니다.
     */
    RESTORE_SESSION(state, { contentId }) {
      // 모든 보고서의 열린 세션을 최소화
      allSessions(state.sessions).forEach(session => {
        if (session.contentId !== contentId && !session.isMinimized) {
          session.isMinimized = true;
        }
      });
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.isMinimized = false;
      }
    },

    /** Worker 성공 응답 시 호출. aiRewriteWorkerManager의 message handler에서 커밋됩니다. */
    SET_RESPONSE(state, { contentId, aiResponse }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.aiResponse = aiResponse;
        entry.session.status = 'completed';
        entry.session.error = null;
        entry.session.toastShown = false;
      }
    },

    /** Worker 에러 시 호출. aiRewriteWorkerManager의 message handler에서 커밋됩니다. */
    SET_ERROR(state, { contentId, error }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.error = error;
        entry.session.status = 'failed';
      }
    },

    /** 최소화 카드에 질문 내역을 표시하기 위해 요청 텍스트를 저장합니다. */
    SET_REQUEST_TEXT(state, { contentId, requestText }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.requestText = requestText;
      }
    },

    /** 응답 화면에 어떤 모드로 요청했는지 표시하기 위해 요청 시점의 모드를 저장합니다. */
    SET_REQUESTED_MODE(state, { contentId, mode }) {
      const entry = findSessionEntry(state.sessions, contentId);
      if (entry) {
        entry.session.requestedMode = mode;
      }
    },

    /**
     * 새로고침 후 IndexedDB에서 복원된 세션을 정리합니다.
     * store/index.js에서 replaceState() 직후 호출됩니다.
     *
     * - idle/loading 세션: Worker(Web Worker)가 새로고침으로 소멸되어 재개 불가 → 삭제
     * - completed/failed 세션: AI 응답이 이미 도착한 상태 → 최소화 카드로 보존
     */
    SANITIZE_RESTORED(state) {
      const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24시간
      const now = Date.now();
      Object.keys(state.sessions).forEach(reportCode => {
        const group = state.sessions[reportCode];
        Object.keys(group).forEach(contentId => {
          const session = group[contentId];
          if (session.createdAt && now - session.createdAt > MAX_AGE_MS) {
            Vue.delete(group, contentId);
            return;
          }
          if (session.status === 'completed' || session.status === 'failed') {
            session.isMinimized = true;
          } else {
            Vue.delete(group, contentId);
          }
        });
        // 빈 보고서 그룹 정리
        if (Object.keys(group).length === 0) {
          Vue.delete(state.sessions, reportCode);
        }
      });
      state.chatLoadingCount = 0;
      state.pendingRestore = null;
      state.pendingApply = null;
      state.pendingScrollTo = null;
    },

    /**
     * 다른 주제의 세션 복원 시 사용.
     * AiRewriteMinimizedStack에서 설정 → WriteStoryBoard의 disclosures watcher에서 소비합니다.
     */
    SET_PENDING_RESTORE(state, { contentId }) {
      state.pendingRestore = contentId;
    },

    CLEAR_PENDING_RESTORE(state) {
      state.pendingRestore = null;
    },

    /**
     * '이걸로 본문 수정하기' 시 사용.
     * MinimizedStack에서 설정 → 주제 이동 → WriteStoryBoard의 disclosures watcher에서 소비합니다.
     */
    SET_PENDING_APPLY(state, { contentId, aiResponse }) {
      state.pendingApply = { contentId, aiResponse };
    },

    CLEAR_PENDING_APPLY(state) {
      state.pendingApply = null;
    },

    /**
     * DataTable 적용 후 다른 주제로 이동 시 스크롤 대상 설정.
     * AiRewriteMinimizedStack에서 설정 → WriteStoryBoard의 disclosures watcher에서 소비합니다.
     */
    SET_PENDING_SCROLL(state, { contentId }) {
      state.pendingScrollTo = contentId;
    },

    CLEAR_PENDING_SCROLL(state) {
      state.pendingScrollTo = null;
    },

    /** AiHelper 채팅에서 요청 시작 시 호출 */
    INCREMENT_CHAT_LOADING(state) {
      state.chatLoadingCount++;
    },

    /** AiHelper 채팅에서 응답/에러 수신 시 호출 */
    DECREMENT_CHAT_LOADING(state) {
      state.chatLoadingCount = Math.max(0, state.chatLoadingCount - 1);
    }
  },

  getters: {
    /** contentId로 세션을 검색 (모든 보고서에서) */
    getSession: state => contentId => {
      const entry = findSessionEntry(state.sessions, contentId);
      return entry ? entry.session : null;
    },

    /** 세션의 주제 경로 문자열을 반환 (예: '환경 > 온실가스 배출량') */
    getTopicPath:
      () =>
      (session, fallback = '') => {
        const lv1 = session?.lv1Name;
        const lv2 = session?.lv2Name;
        if (lv1 && lv2) return `${lv1} > ${lv2}`;
        if (lv1) return lv1;
        return fallback;
      },

    /** 모든 보고서의 최소화된 세션 목록 */
    minimizedSessions(state) {
      return allSessions(state.sessions)
        .filter(s => s.isMinimized)
        .sort((a, b) => String(a.contentId).localeCompare(String(b.contentId)));
    },

    /**
     * 특정 보고서의 최소화된 세션만 반환.
     * AiRewriteMinimizedStack에서 현재 보고서의 세션만 표시할 때 사용합니다.
     */
    minimizedSessionsByReport: state => reportCode => {
      const group = state.sessions[reportCode];
      if (!group) return [];
      return Object.values(group)
        .filter(s => s.isMinimized)
        .sort((a, b) => String(a.contentId).localeCompare(String(b.contentId)));
    },

    /**
     * AI 재작성 세션의 loading 수 + AiHelper 채팅 로딩 수.
     * WriteStoryBoard의 isAiLoading computed에서 사용합니다.
     */
    loadingCount(state) {
      return (
        allSessions(state.sessions).filter(s => s.status === 'loading').length +
        state.chatLoadingCount
      );
    },

    /** 현재 열려있는(최소화되지 않은) 세션 목록 */
    visibleSessions(state) {
      return allSessions(state.sessions).filter(s => !s.isMinimized);
    },

    /**
     * loading 세션이 하나라도 있는지 여부.
     * WriteNav에서 목차 이동 차단, AiRewriteMinimizedStack에서 참조합니다.
     */
    hasLoadingSessions(state) {
      return (
        allSessions(state.sessions).some(s => s.status === 'loading') || state.chatLoadingCount > 0
      );
    },

    /**
     * 페이지 이동 시 경고를 표시할지 결정하는 getter.
     * loading 세션만 차단 대상입니다.
     */
    hasActiveSessions(state, getters) {
      return getters.hasLoadingSessions;
    },

    /** 전체 세션 수 (모든 보고서) — 경고 표시 여부 판단에 사용 */
    totalSessionCount(state) {
      return allSessions(state.sessions).length;
    },

    /** 특정 보고서의 세션 수 */
    sessionCountByReport: state => reportCode => {
      const group = state.sessions[reportCode];
      if (!group) return 0;
      return Object.keys(group).length;
    },

    /** 특정 보고서에 loading 세션이 있는지 여부 */
    hasLoadingSessionsByReport: state => reportCode => {
      const group = state.sessions[reportCode];
      if (!group) return false;
      return Object.values(group).some(s => s.status === 'loading');
    }
  }
};
